/* eslint-disable */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

const ProjectClosure: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { userProfile } = useAuth();
  const companyId = userProfile?.company_id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [project, setProject] = useState<any>(null);
  const [milestones, setMilestones] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<any[]>([]);
  const [canClose, setCanClose] = useState(false);

  useEffect(() => {
    if (projectId) {
      loadProject();
      loadMilestones();
      loadMetrics();
    }
  }, [projectId]);

  const loadProject = async () => {
    if (!projectId) return;

    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .single();

    if (!error && data) {
      setProject(data);
    }
  };

  const loadMilestones = async () => {
    if (!projectId) return;

    const { data, error } = await supabase
      .from('project_milestones')
      .select('*')
      .eq('project_id', projectId);

    if (!error && data) {
      setMilestones(data);
    }
  };

  const loadMetrics = async () => {
    if (!projectId) return;

    const { data, error } = await supabase
      .from('project_metrics_progress')
      .select('*')
      .eq('project_id', projectId);

    if (!error && data) {
      setMetrics(data);
    }
  };

  useEffect(() => {
    // Check if project can be closed
    const allMilestonesDone = milestones.length > 0 && milestones.every((m: any) => m.status === 'completed');
    const finalKPISubmitted = metrics.length > 0; // Check if final KPI report exists
    const finalUCApproved = true; // TODO: Check final UC/SoE approval status
    
    setCanClose(allMilestonesDone && finalKPISubmitted && finalUCApproved);
  }, [milestones, metrics]);

  const handleClose = async () => {
    if (!canClose) {
      setError('Cannot close project. Please ensure all milestones are completed, final KPI is submitted, and final UC/SoE is approved.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !projectId) throw new Error('Not authenticated');

      // Create project closure record
      const { error: closureError } = await supabase
        .from('project_closure')
        .insert({
          project_id: projectId,
          closed_by: user.id,
          closed_at: new Date().toISOString(),
          closure_summary: `Project closed on ${new Date().toLocaleDateString()}`
        });

      if (closureError) throw closureError;

      // Update project status
      const { error: updateError } = await supabase
        .from('projects')
        .update({ status: 'closed' })
        .eq('id', projectId);

      if (updateError) throw updateError;

      navigate(`/client/company/projects/${projectId}`);
    } catch (err: any) {
      setError(err.message || 'Failed to close project');
    } finally {
      setLoading(false);
    }
  };

  if (!project) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (project.status === 'closed') {
    return (
      <div className="form-container">
        <div className="form-section">
          <h1>Project Closed</h1>
          <div className="card">
            <p>This project has been closed and is now read-only.</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/client/company/projects/${projectId}`)}
            >
              View Project
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-section">
        <h1>Close Project</h1>
        <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
          Review project completion status: <strong>{project.name}</strong>
        </p>

        {error && (
          <div className="toast toast-error" style={{ position: 'relative', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <div className="card">
          <h2>Closure Requirements</h2>
          
          <div className="checklist-item" style={{ marginBottom: '1rem' }}>
            <input
              type="checkbox"
              checked={milestones.length > 0 && milestones.every((m: any) => m.status === 'completed')}
              disabled
            />
            <div className="checklist-item-info">
              <strong>All Milestones Completed</strong>
              <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--muted)' }}>
                {milestones.length > 0 
                  ? `${milestones.filter((m: any) => m.status === 'completed').length} / ${milestones.length} completed`
                  : 'No milestones defined'}
              </p>
            </div>
          </div>

          <div className="checklist-item" style={{ marginBottom: '1rem' }}>
            <input
              type="checkbox"
              checked={metrics.length > 0}
              disabled
            />
            <div className="checklist-item-info">
              <strong>Final KPI Report Submitted</strong>
              <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--muted)' }}>
                {metrics.length > 0 ? 'Final KPI report available' : 'No final KPI report'}
              </p>
            </div>
          </div>

          <div className="checklist-item" style={{ marginBottom: '1rem' }}>
            <input
              type="checkbox"
              checked={true} // TODO: Check actual UC/SoE approval status
              disabled
            />
            <div className="checklist-item-info">
              <strong>Final UC/SoE Approved</strong>
              <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--muted)' }}>
                Final Utilization Certificate and Statement of Expenditure approved
              </p>
            </div>
          </div>

          <div className="checklist-item">
            <input
              type="checkbox"
              checked={true} // TODO: Check final impact attachment
              disabled
            />
            <div className="checklist-item-info">
              <strong>Final Impact Attached</strong>
              <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--muted)' }}>
                Final impact documentation attached
              </p>
            </div>
          </div>
        </div>

        {!canClose && (
          <div className="toast toast-warning" style={{ position: 'relative', marginTop: '1rem' }}>
            Please complete all requirements before closing the project.
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => navigate(`/client/company/projects/${projectId}`)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleClose}
            disabled={loading || !canClose}
          >
            {loading ? 'Closing...' : 'Close Project'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectClosure;


